
import { Component, Prop, Vue } from "vue-property-decorator";
import dayjs from "dayjs";
import AssigneeCombo from "@/components/Combobox/AssigneeCombo.vue";
import {
  clientsModule,
  commonModule,
  installationsModule,
  jobsModule,
  paymentsModule,
} from "@/store/modules/store-accessor";

type Payer = { id: string | number; name: string; is_business?: boolean };
type AllocationRow = {
  id: string | number;
  installationId: string | number;
  installationLabel: string;
  label: string;
  date: string;
  order: number;
  pending: number;
  selected: boolean;
  payers: Payer[];
};
type AllocationGroup = { key: string; label: string; rows: AllocationRow[] };
type ComputedAllocation = {
  row: AllocationRow;
  jobAmount: number;
  externalAmount: number;
  creditAmount: number;
  depositAmount: number;
};

@Component({ components: { AssigneeCombo } })
export default class QuickAddPaymentsModal extends Vue {
  @Prop({ default: () => ({}) }) payload!: Record<string, any>;

  scope: "client" | "installation" = "client";
  selectedEntity: Record<string, any> | null = null;
  selectedPayer: Payer | null = null;
  rows: AllocationRow[] = [];
  credits: Record<string, number> = {};
  expandedGroups: string[] = [];
  loadingEntities = true;
  loadingClients = false;
  clientsLoaded = commonModule.clientsCombo.length > 0;
  clientLoadError = "";
  loadingJobs = false;
  submitting = false;
  loadError = "";
  submissionError = "";
  viewMode: "form" | "success" = "form";
  clientSelectionConfirmed = false;
  dateMenu = false;
  timeMenu = false;
  paymentAmount: number | string = 0;
  paymentAmountTouched = false;
  applyCreditEnabled = false;
  creditAmount: number | string = 0;
  details = {
    paymentType: null as Record<string, any> | null,
    date: dayjs().format("YYYY-MM-DD"),
    time: dayjs().format("HH:mm"),
    assignee: {
      id: commonModule.user.uuid,
      name: commonModule.user.name,
    } as any,
    notes: "",
  };
  submittedSummary = {
    payment: 0,
    paymentType: "",
    date: "",
    jobs: "",
    partial: "",
    creditUsed: 0,
    credit: 0,
    remaining: 0,
    newCredit: null as number | null,
  };

  notesRule = [
    (value: string) =>
      !value || value.length <= 1000 || this.$t("form_notes_length_rule"),
  ];

  get amountRules() {
    return [
      (value: number | string) =>
        this.numberValue(value) > 0 ||
        this.appliedCredit > 0 ||
        this.$t("nonZeroAmountRuleText"),
    ];
  }

  get creditRules() {
    return [
      (value: number | string) =>
        this.numberValue(value) >= 0 || this.$t("nonZeroAmountRuleText"),
      (value: number | string) =>
        this.numberValue(value) <= this.availableCredit + 0.001 ||
        this.$t("availableCreditAmountRuleText"),
      (value: number | string) =>
        this.numberValue(value) <= this.selectedPendingTotal + 0.001 ||
        this.$t("creditExceedsJobBalance"),
    ];
  }

  async mounted() {
    commonModule.initSnackbar({});
    if (this.payload.scope)
      this.scope =
        this.payload.scope === "installation" ? "installation" : "client";
    try {
      await Promise.all([
        commonModule
          .getPaymentTypeCombo()
          .then(() => (this.details.paymentType = this.paymentTypes[0] || null)),
        this.isPreScoped ? this.applyPreScope() : this.ensureClientsLoaded(),
      ]);
    } finally {
      this.loadingEntities = false;
    }
  }

  get isPreparing() {
    return this.loadingEntities || (this.isPreScoped && this.loadingJobs);
  }

  get paymentTypes() {
    return commonModule.paymentTypeCombo;
  }
  get isPreScoped() {
    return Boolean(this.payload.scope);
  }
  get clientItems() {
    return commonModule.clientsCombo;
  }
  get stepperStep() {
    return this.clientSelectionConfirmed ? 2 : 1;
  }

  get selectedRows() {
    return this.rows.filter((row) => row.selected);
  }
  get sortedRows() {
    return [...this.rows].sort(this.compareRows);
  }
  get sortedSelectedRows() {
    return [...this.selectedRows].sort(this.compareRows);
  }

  get groupedRows(): AllocationGroup[] {
    const groups: Record<string, AllocationGroup> = {};
    this.sortedRows.forEach((row) => {
      const key = String(row.installationId);
      if (!groups[key])
        groups[key] = { key, label: row.installationLabel, rows: [] };
      groups[key].rows.push(row);
    });
    return Object.keys(groups).map((key) => groups[key]);
  }

  get payerOptions() {
    const payers: Record<string, Payer> = {};
    if (this.scope === "client" && this.selectedEntity) {
      const client = this.normalizePayer(this.selectedEntity);
      if (client.id != null) payers[String(client.id)] = client;
      return Object.keys(payers).map((id) => payers[id]);
    }
    if (this.scope === "installation" && this.selectedEntity) {
      this.installationClients(this.selectedEntity).forEach((client: any) => {
        const normalized = this.normalizePayer(client);
        payers[String(normalized.id)] = normalized;
      });
      return Object.keys(payers).map((id) => payers[id]);
    }
    this.rows.forEach((row) =>
      row.payers.forEach((payer) => {
        if (payer.id != null) payers[String(payer.id)] = payer;
      })
    );
    return Object.keys(payers).map((id) => payers[id]);
  }

  get selectedPendingTotal() {
    return this.roundMoney(
      this.selectedRows.reduce((total, row) => total + row.pending, 0)
    );
  }
  get paymentTotal() {
    return this.roundMoney(Math.max(this.numberValue(this.paymentAmount), 0));
  }
  get availableCredit() {
    if (!this.selectedPayer) return 0;
    return this.roundMoney(this.credits[String(this.selectedPayer.id)] || 0);
  }
  get creditLimit() {
    return this.roundMoney(
      Math.min(this.availableCredit, this.selectedPendingTotal)
    );
  }
  get maximumCredit() {
    return this.roundMoney(
      Math.min(
        this.availableCredit,
        Math.max(this.selectedPendingTotal - this.paymentTotal, 0)
      )
    );
  }
  get appliedCredit() {
    if (!this.applyCreditEnabled || this.overpayment > 0) return 0;
    return this.roundMoney(
      Math.min(Math.max(this.numberValue(this.creditAmount), 0), this.maximumCredit)
    );
  }
  get externalAmount() {
    return this.paymentTotal;
  }
  get overpayment() {
    if (this.isPrepayment || !this.selectedRows.length) return 0;
    return this.roundMoney(Math.max(this.paymentTotal - this.selectedPendingTotal, 0));
  }
  get creditDepositTotal() {
    return this.isCreditOnly ? this.paymentTotal : this.overpayment;
  }
  get jobPaymentTotal() {
    return this.roundMoney(
      Math.min(this.paymentTotal + this.appliedCredit, this.selectedPendingTotal)
    );
  }
  get remainingPending() {
    return this.roundMoney(Math.max(this.allPendingTotal - this.jobPaymentTotal, 0));
  }
  get allPendingTotal() {
    return this.roundMoney(this.rows.reduce((total, row) => total + row.pending, 0));
  }

  get amountHint() {
    if (this.isCreditOnly) return String(this.$t("bulkPaymentAllToCreditHint"));
    const owed = this.roundMoney(Math.max(this.selectedPendingTotal - this.appliedCredit, 0));
    return String(
      this.$t("bulkPaymentOwedForSelected", { amount: this.formatCurrency(owed) })
    );
  }

  get computedAllocations(): ComputedAllocation[] {
    let creditRemaining = this.appliedCredit;
    let externalRemaining = this.externalAmount;
    const allocations: ComputedAllocation[] = [];
    this.sortedSelectedRows.forEach((row) => {
      const creditAmount = this.roundMoney(Math.min(row.pending, creditRemaining));
      creditRemaining = this.roundMoney(creditRemaining - creditAmount);
      const externalAmount = this.roundMoney(Math.min(row.pending - creditAmount, externalRemaining));
      externalRemaining = this.roundMoney(externalRemaining - externalAmount);
      const jobAmount = this.roundMoney(creditAmount + externalAmount);
      if (jobAmount > 0)
        allocations.push({
          row,
          jobAmount,
          externalAmount,
          creditAmount,
          depositAmount: 0,
        });
    });
    if (externalRemaining > 0 && allocations.length) {
      allocations[allocations.length - 1].depositAmount = externalRemaining;
    }
    return allocations;
  }

  get fullyPaidCount() {
    return this.computedAllocations.filter((allocation) => allocation.jobAmount >= allocation.row.pending - 0.001).length;
  }
  get partialAllocation() {
    return this.computedAllocations.find(
      (allocation) =>
        allocation.jobAmount > 0 &&
        allocation.jobAmount < allocation.row.pending - 0.001
    );
  }
  get isPrepayment() {
    return (
      Boolean(this.selectedEntity) &&
      !this.loadingJobs &&
      this.rows.length === 0
    );
  }
  get isCreditOnly() {
    return this.isPrepayment || !this.selectedRows.length;
  }
  get showAllocationPreview() {
    return !this.isPrepayment && this.computedAllocations.length > 0;
  }
  get canReview() {
    if (!this.selectedEntity || this.loadingJobs) return false;
    if (this.paymentTotal <= 0 && this.appliedCredit <= 0) return false;
    if (!this.details.paymentType || !this.selectedPayer) return false;
    if (
      this.applyCreditEnabled &&
      this.numberValue(this.creditAmount) > this.creditLimit + 0.001
    )
      return false;
    return !this.details.notes || this.details.notes.length <= 1000;
  }
  get activeClientName() {
    if (this.scope === "client") return this.clientName(this.selectedEntity);
    return this.selectedPayer
      ? this.selectedPayer.name
      : this.clientName(this.selectedEntity);
  }
  get selectionCountLabel() {
    return this.$t("bulkPaymentSelectedOf", {
      selected: this.selectedRows.length,
      total: this.rows.length,
    });
  }
  get reviewJobsHint() {
    return this.$t("bulkPaymentPaidBreakdown", {
      full: this.fullyPaidCount,
      partial: this.partialAllocation ? 1 : 0,
    });
  }
  get paymentBreakdown() {
    const cashToJobs = this.roundMoney(this.jobPaymentTotal - this.appliedCredit);
    if (this.appliedCredit <= 0) return "";
    return [
      cashToJobs > 0 ? `${this.formatCurrency(cashToJobs)} ${this.selectedPaymentTypeLabel}` : "",
      String(this.$t("paymentBreakdownCredit", { amount: this.formatCurrency(this.appliedCredit) })),
    ].filter(Boolean).join(" + ");
  }
  get selectedPaymentTypeLabel() {
    return this.details.paymentType
      ? this.details.paymentType.payment_type
      : String(this.$t("bulkPaymentNotSelected"));
  }
  get formattedPaymentDate() {
    return dayjs(`${this.details.date} ${this.details.time}`).format(
      "DD/MM/YYYY HH:mm"
    );
  }
  async applyPreScope() {
    if (!this.payload.scope) return;
    this.scope =
      this.payload.scope === "installation" ? "installation" : "client";

    const id =
      this.scope === "client"
        ? this.payload.clientId
        : this.payload.installationId;

    this.selectedEntity =
      this.payload.entity ||
      (this.scope === "client"
        ? commonModule.clientsCombo
        : commonModule.installationsCombo
      ).find((entity: any) => String(entity.id) === String(id)) ||
      null;

    if (!this.selectedEntity) {
      if (this.scope === "client") {
        await commonModule.getClientsCombo();
        this.clientsLoaded = true;
        this.selectedEntity =
          commonModule.clientsCombo.find(
            (entity: any) => String(entity.id) === String(id)
          ) || null;
      } else {
        await commonModule.getInstallationsCombo();
        this.selectedEntity =
          commonModule.installationsCombo.find(
            (entity: any) => String(entity.id) === String(id)
          ) || null;
      }
    }
    if (this.selectedEntity) {
      await this.fetchPendingJobs();
      this.clientSelectionConfirmed = this.scope === "client";
    }
  }

  async ensureClientsLoaded() {
    if (this.clientsLoaded) return;
    this.loadingClients = true;
    this.clientLoadError = "";
    try {
      await commonModule.getClientsCombo();
      this.clientsLoaded = true;
    } catch {
      this.clientLoadError = String(this.$t("bulkPaymentClientsLoadFailed"));
    } finally {
      this.loadingClients = false;
    }
  }
  async clientSelectionChanged(client: any) {
    this.clientSelectionConfirmed = false;
    if (!client) {
      this.selectedEntity = null;
      this.resetPaymentState();
      return;
    }
    this.scope = "client";
    this.selectedEntity = client;
    await this.fetchPendingJobs();
  }

  async fetchPendingJobs() {
    if (!this.selectedEntity) return;
    this.loadingJobs = true;
    this.loadError = "";
    this.resetPaymentState();
    try {
      const key = this.scope === "client" ? "client_id" : "installation_id";
      const [response]: any[] = await Promise.all([
        paymentsModule.getPendingJobs({ [key]: this.selectedEntity.id }),
        this.loadCredits(),
      ]);
      const jobs = Array.isArray(response)
        ? response
        : (response && (response.jobs || response.pending_jobs)) || [];

      this.rows = jobs
        .map((job: any, index: number) => this.normalizeJob(job, index))
        .filter((row: AllocationRow) => row.pending > 0);

      this.selectedPayer =
        this.scope === "installation" ? null : this.defaultPayer();

      this.expandedGroups =
        this.groupedRows.length === 1 ? [this.groupedRows[0].key] : [];
      this.paymentAmount = this.selectedPendingTotal;
    } catch {
      this.rows = [];
      this.loadError = String(this.$t("bulkPaymentLoadFailed"));
    } finally {
      this.loadingJobs = false;
    }
  }

  continueToPayment() {
    if (
      this.selectedEntity &&
      !this.loadingJobs &&
      !this.loadError &&
      (this.scope !== "installation" || this.selectedPayer)
    )
      this.clientSelectionConfirmed = true;
  }
  backToClientSelection() {
    this.clientSelectionConfirmed = false;
  }

  normalizeJob(job: any, order: number): AllocationRow {
    const pending = Math.max(
      this.numberValue(
        job.pending ??
          job.pending_balance ??
          job.balance ??
          this.numberValue(job.total_cost ?? job.cost) -
            this.numberValue(job.paid_amount ?? job.amount_paid)
      ),
      0
    );
    const installationId =
      job.installation_id ??
      job.installation?.id ??
      (this.scope === "installation" && this.selectedEntity
        ? this.selectedEntity.id
        : "");

    const installationLabel =
      job.installation_name ||
      this.installationName({
        ...(job.installation || {}),
        id: installationId,
        tag_id: job.installation_tag || job.installation?.tag_id,
        formatted_address:
          job.formatted_address || job.installation?.formatted_address,
      });
    const id = job.id ?? job.job_id;
    const description =
      job.title ||
      job.description ||
      job.item_description ||
      (Array.isArray(job.type_name) ? job.type_name.join(", ") : job.type_name);
    return {
      id,
      installationId,
      installationLabel,
      label: description || String(this.$t("job")),
      date: job.last_job_entry_at || job.completed_at || job.created_at || "",
      order,
      pending: this.roundMoney(pending),
      selected: true,
      payers: this.jobPayers(job),
    };
  }

  compareRows(left: AllocationRow, right: AllocationRow) {
    if (left.date && right.date) {
      const difference =
        dayjs(left.date).valueOf() - dayjs(right.date).valueOf();
      if (difference) return difference;
    }
    if (left.date) return -1;
    if (right.date) return 1;
    return left.order - right.order;
  }

  installationClients(installation: any) {
    return [
      installation.user_id
        ? {
            id: installation.user_id,
            firstname: installation.client_firstname,
            lastname: installation.client_lastname,
            entity_name: installation.client_entity_name,
            is_business: installation.client_is_business,
          }
        : null,
      installation.owner_id
        ? {
            id: installation.owner_id,
            firstname: installation.owner_firstname,
            lastname: installation.owner_lastname,
            entity_name: installation.owner_entity_name,
            is_business: installation.owner_is_business,
          }
        : null,
    ].filter(Boolean);
  }

  jobPayers(job: any): Payer[] {
    if (this.scope === "client" && this.selectedEntity)
      return [this.normalizePayer(this.selectedEntity)];
    let payers = job.clients || job.payers || [];
    if (!payers.length) payers = [job.client, job.owner].filter(Boolean);
    if (!payers.length && this.selectedEntity)
      payers = this.installationClients(this.selectedEntity);
    if (!payers.length && job.client_id)
      payers = [{ id: job.client_id, name: job.client_name }];
    const unique: Record<string, Payer> = {};
    payers.forEach((payer: any) => {
      const normalized = this.normalizePayer(payer);
      if (normalized.id != null) unique[String(normalized.id)] = normalized;
    });
    return Object.keys(unique).map((id) => unique[id]);
  }

  normalizePayer(payer: any): Payer {
    return {
      id: payer.id ?? payer.client_id ?? payer.user_id,
      name: payer.name || this.clientName(payer),
      is_business: payer.is_business,
    };
  }
  defaultPayer() {
    if (this.scope === "client" && this.selectedEntity)
      return this.normalizePayer(this.selectedEntity);
    return this.payerOptions[0] || null;
  }
  async loadCredits() {
    await Promise.all(
      this.payerOptions.map(async (payer) => {
        try {
          const credit = await clientsModule.getClientCredit(String(payer.id));
          this.$set(
            this.credits,
            String(payer.id),
            this.numberValue(credit && credit.available_credit)
          );
        } catch {
          this.$set(this.credits, String(payer.id), 0);
        }
      })
    );
  }

  resetPaymentState() {
    this.rows = [];
    this.credits = {};
    this.selectedPayer = null;
    this.paymentAmount = 0;
    this.paymentAmountTouched = false;
    this.applyCreditEnabled = false;
    this.creditAmount = 0;
    this.viewMode = "form";
    this.clientSelectionConfirmed = false;
    this.submissionError = "";
  }
  selectionChanged() {
    if (!this.selectedRows.length) {
      this.applyCreditEnabled = false;
      this.creditAmount = 0;
      this.paymentAmount = 0;
      this.paymentAmountTouched = false;
      return;
    }
    this.paymentAmountTouched = false;
    if (this.applyCreditEnabled) {
      this.creditToggleChanged(true);
      return;
    }
    this.paymentAmount = this.selectedPendingTotal;
  }
  get allRowsSelected() {
    return this.rows.length > 0 && this.rows.every((row) => row.selected);
  }
  toggleAllRows() {
    const selected = !this.allRowsSelected;
    this.rows.forEach((row) => {
      row.selected = selected;
    });
    this.selectionChanged();
  }
  toggleGroup(group: AllocationGroup, selected: boolean) {
    group.rows.forEach((row) => {
      row.selected = Boolean(selected);
    });
    this.selectionChanged();
  }
  groupAllSelected(group: AllocationGroup) {
    return group.rows.length > 0 && group.rows.every((row) => row.selected);
  }
  groupPartiallySelected(group: AllocationGroup) {
    const count = group.rows.filter((row) => row.selected).length;
    return count > 0 && count < group.rows.length;
  }
  groupPending(group: AllocationGroup) {
    return this.roundMoney(
      group.rows
        .filter((row) => row.selected)
        .reduce((total, row) => total + row.pending, 0)
    );
  }
  groupSelectionLabel(group: AllocationGroup) {
    return this.$t("bulkPaymentGroupJobs", {
      selected: group.rows.filter((row) => row.selected).length,
      total: group.rows.length,
    });
  }
  toggleGroupExpanded(key: string) {
    this.expandedGroups = this.isGroupExpanded(key)
      ? this.expandedGroups.filter((value) => value !== key)
      : [...this.expandedGroups, key];
  }
  isGroupExpanded(key: string) {
    return this.expandedGroups.includes(key);
  }
  paymentAmountChanged() {
    this.paymentAmountTouched = true;
    this.normalizeCreditAmount();
    this.$nextTick(() => this.validateCreditAmount());
  }
  creditToggleChanged(enabled: boolean) {
    if (enabled && this.paymentAmountTouched) {
      this.creditAmount = this.maximumCredit;
    } else if (enabled) {
      this.creditAmount = this.creditLimit;
      this.paymentAmount = this.roundMoney(
        Math.max(this.selectedPendingTotal - this.creditLimit, 0)
      );
    } else {
      this.creditAmount = 0;
      if (!this.paymentAmountTouched) this.paymentAmount = this.selectedPendingTotal;
    }
  }
  creditAmountChanged(value: number | string) {
    if (this.paymentAmountTouched) return;
    const credit = this.numberValue(value);
    if (credit < 0 || credit > this.creditLimit) return;
    this.paymentAmount = this.roundMoney(
      Math.max(this.selectedPendingTotal - credit, 0)
    );
  }
  normalizeCreditAmount() {
    if (this.overpayment > 0) {
      this.creditAmount = 0;
      this.applyCreditEnabled = false;
      return;
    }
    if (this.applyCreditEnabled)
      this.creditAmount = Math.min(
        this.numberValue(this.creditAmount),
        this.maximumCredit
      );
  }
  validateCreditAmount() {
    const field = this.$refs.creditAmountField as Vue & {
      validate?: () => boolean;
    };
    if (field && typeof field.validate === "function") field.validate();
  }
  payerChanged() {
    this.applyCreditEnabled = false;
    this.creditAmount = 0;
  }
  submitFromForm() {
    if (this.submitting) return;
    const form = this.$refs.paymentForm as Vue & { validate: () => boolean };
    if (form && form.validate() && this.canReview) {
      this.submissionError = "";
      this.submit();
    }
  }

  async submit() {
    if (!this.canReview || !this.selectedPayer || !this.details.paymentType) {
      return;
    }
    this.submitting = true;
    this.submissionError = "";
    try {
      const createdAt = dayjs(
        `${this.details.date} ${this.details.time}`
      ).toISOString();
      if (this.isCreditOnly) {
        await clientsModule.createCreditDeposit({
          clientId: String(this.selectedPayer.id),
          data: {
            amount: this.paymentTotal,
            payment_type_id: this.details.paymentType.id,
            notes: this.details.notes,
            created_at: createdAt,
          },
        });
      } else {
        const assignee: any = this.details.assignee;
        const data: BulkJobPaymentsRequestDto = {
          created_at: createdAt,
          assignee_id: assignee && assignee.id ? assignee.id : assignee || null,
          payment_type_id: this.details.paymentType.id,
          client_id: this.selectedPayer.id,
          notes: this.details.notes,
          allocations: this.computedAllocations.map((allocation) => ({
            job_id: allocation.row.id,
            installation_id: allocation.row.installationId,
            amount: allocation.externalAmount,
            apply_credit_amount: allocation.creditAmount,
            credit_deposit_amount: allocation.depositAmount,
          })),
        };
        await jobsModule.createBulkPayments(data);
      }
      this.captureSubmittedSummary();
      this.viewMode = "success";
      try {
        await this.refreshAfterSuccess();
      } catch {
        return 0;
      }
    } catch (error) {
      this.submissionError = error?.response?.data?.error || String(this.$t("bulkPaymentsFailed"));
    } finally {
      this.submitting = false;
    }
  }

  captureSubmittedSummary() {
    const partial = this.partialAllocation;
    this.submittedSummary = {
      payment: this.paymentTotal,
      paymentType: this.selectedPaymentTypeLabel,
      date: this.formattedPaymentDate,
      jobs: this.isCreditOnly
        ? ""
        : String(
            this.$t("bulkPaymentJobsAndAmount", {
              count: this.fullyPaidCount,
              amount: this.formatCurrency(this.jobPaymentTotal),
            })
          ),
      partial: partial
        ? `${partial.row.label} - ${this.formatCurrency(partial.jobAmount)}`
        : "",
      creditUsed: this.appliedCredit,
      credit: this.creditDepositTotal,
      remaining: this.isCreditOnly ? 0 : this.remainingPending,
      newCredit:
        this.isCreditOnly || this.overpayment > 0 || this.appliedCredit > 0
          ? this.roundMoney(
              this.availableCredit + this.creditDepositTotal - this.appliedCredit
            )
          : null,
    };
  }

  async refreshAfterSuccess() {
    if (this.scope === "installation" && this.selectedEntity) {
      const installationId = String(this.selectedEntity.id);
      await Promise.all([
        installationsModule.getInstallationDetailsJobs(installationId),
        installationsModule.getInstallationDetailsFinancials(installationId),
      ]);
      if (this.selectedPayer) {
        await clientsModule.getClientCredit(String(this.selectedPayer.id));
      }
    }
    const openClientId = (clientsModule.currentClient as any)?.info?.id;
    if (
      this.scope === "client" &&
      this.selectedEntity &&
      String(openClientId) === String(this.selectedEntity.id)
    ) {
      const clientId = String(this.selectedEntity.id);
      await Promise.all([
        clientsModule.getClientsInfo(clientId),
        clientsModule.loadCurrentClientCredit(clientId),
      ]);
    }
    if (this.$route.name === "Jobs")
      await jobsModule.getJobs(jobsModule.filters);
    if (this.$route.name === "My Company") {
      await Promise.all([
        paymentsModule.getPaymentsAction(paymentsModule.filters),
        paymentsModule.getPaymentsTotals(paymentsModule.filters),
      ]);
    }
  }

  async startNewPayment() {
    this.viewMode = "form";
    this.details.date = dayjs().format("YYYY-MM-DD");
    this.details.time = dayjs().format("HH:mm");
    this.details.notes = "";
    if (this.isPreScoped && this.selectedEntity) {
      await this.fetchPendingJobs();
      this.clientSelectionConfirmed = this.scope === "client";
      return;
    }
    this.selectedEntity = null;
    this.resetPaymentState();
  }

  clientName(client: any) {
    if (!client) return "";
    const person = [client.lastname, client.firstname]
      .filter(Boolean)
      .join(" ");
    return client.is_business && client.entity_name
      ? `${client.entity_name}${person ? ` (${person})` : ""}`
      : client.name || client.fullname || person || String(client.id || "");
  }
  installationName(installation: any) {
    if (!installation) return "";
    const tag = installation.tag_id
      ? `#${String(installation.tag_id).split("_").pop()}`
      : installation.id
      ? `#${installation.id}`
      : "";
    const address =
      installation.formatted_address ||
      [installation.address, installation.area, installation.zip_code]
        .filter(Boolean)
        .join(", ");
    return (
      [tag, address].filter(Boolean).join(" - ") ||
      String(installation.id || "")
    );
  }
  formatJobDate(value: string) {
    return value && dayjs(value).isValid()
      ? dayjs(value).format("DD/MM/YYYY HH:mm")
      : "";
  }
  allocationStatus(allocation: ComputedAllocation) {
    return allocation.jobAmount >= allocation.row.pending - 0.001
      ? this.$t("bulkPaymentPaidInFull")
      : this.$t("bulkPaymentPartialAmount", {
          amount: this.formatCurrency(
            allocation.row.pending - allocation.jobAmount
          ),
        });
  }
  numberValue(value: any) {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  }
  roundMoney(value: number) {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }
  formatCurrency(value: number) {
    return `${new Intl.NumberFormat(
      this.$i18n.locale === "gr" ? "el-GR" : "en-GB",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    ).format(Number(value) || 0)} €`;
  }
  updateAssignee(assignee: any) {
    if (assignee) this.details.assignee = assignee;
  }
  hideModal() {
    commonModule.hideModal();
  }
}
